import {
  Component,
  OnInit,
  Inject,
  ChangeDetectionStrategy,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BookmarkModel } from 'src/app/bookmarks/models/bookmark.model';
import { Select, Store, Actions, ofActionDispatched } from '@ngxs/store';
import { OrganizationsState } from 'src/app/organizations/state/organizations.state';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { OrganizationModel } from 'src/app/organizations/models/organization.model';
import { CollectionModel } from 'src/app/organizations/models/collection.model';
import { FormGroup, FormBuilder, Validators, FormArray } from '@angular/forms';
import { BookmarkGroupModel } from 'src/app/bookmarks/models/bookmark.group.model';
import {
  SaveBookmark,
  SaveBookmarkSuccess,
  SaveBookmarkFail,
  CopyBookmark,
  CopyBookmarkSuccess,
  CopyBookmarkFailure,
  CancelCopyBookmark,
  CancelSaveBookmark,
  UpdateBookmarkCollections,
  UpdateBookmarkCollectionsSuccess,
  UpdateBookmarkCollectionsError,
} from 'src/app/bookmarks/state/bookmarks.actions';
import { OrganizationsService } from 'src/app/organizations/organizations.service';
import { debounceTime, take, filter } from 'rxjs/operators';
import { takeUntil } from 'rxjs/operators';
import { BookmarkEntryModel } from 'src/app/bookmarks/models/bookmark.entry.model';
import { GetCollections } from 'src/app/organizations/state/organizations.actions';
import { BookmarksState } from 'src/app/bookmarks/state/bookmarks.state';
import { StoryService } from 'src/app/story/story.service';
import { StoryModel } from 'src/app/story/models/story.model';
import { get, isEqual, isNil } from 'lodash';
import { AuthState } from 'src/app/auth/state/auth/auth.state';
import { DialogActions } from '../../state/dialog.actions';
import { UserRoleModel } from 'src/app/auth/models/user-role.model';
import { PermissionsUtil } from 'src/app/auth/models/permissions/permissions-util';
import { UserPermissionEnum } from 'src/app/auth/models/permissions/user-permission.enum';
import { BookmarkViewModeTag } from 'src/app/bookmarks/models/bookmark-view-mode.enum';
import { GalleryActions } from 'src/app/gallery/state/gallery.actions';

export enum SaveBookmarkDialogMode {
  SaveStory,
  Manage,
  Copy,
}

export interface SaveBookmarkDialogData {
  bookmark?: BookmarkModel;
  title?: string;
  bookmarkTitle?: string;
  viewMode?: BookmarkViewModeTag;
  message?: string;
  mode?: SaveBookmarkDialogMode;
}

@Component({
  selector: 'app-save-bookmark-dialog',
  templateUrl: './save-bookmark-dialog.component.html',
  styleUrls: ['./save-bookmark-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SaveBookmarkDialogComponent implements OnInit, OnDestroy {
  @Select(AuthState.getUserRole)
  userRole$: Observable<UserRoleModel>;

  @Select(OrganizationsState.getOrganization)
  organization$: Observable<OrganizationModel>;

  @Select(OrganizationsState.getCollections)
  collections$: Observable<CollectionModel[]>;

  @Select(BookmarksState.getSaving)
  bookmarkSaving$: Observable<boolean>;

  @Select(BookmarksState.getCopying)
  bookmarkCopying$: Observable<boolean>;

  get saving$() {
    return this.settings.mode === SaveBookmarkDialogMode.Copy
      ? this.bookmarkCopying$
      : this.bookmarkSaving$;
  }

  get dialogTitle(): string {
    if (this.settings.title) {
      return this.settings.title;
    }

    switch (this.settings.mode) {
      case SaveBookmarkDialogMode.Manage:
        return 'Manage';
      case SaveBookmarkDialogMode.SaveStory:
        return 'Save';
      case SaveBookmarkDialogMode.Copy:
        return 'Add to My Library';
    }
  }

  form: FormGroup;

  disablePristine = false;

  collectionsArray: FormArray;
  collectionsDirty = false;
  nameAccessDirty = false;

  collectionsLoading = false;
  collectionsEnabled = false;

  // Map collection ids to collections
  collectionsMap = new Map<number, CollectionModel>();

  // For a given piece of content, map its collectionIds to
  // Their respective collectionContentId
  contentCollections = new Map<number, number>();

  submit = new Subject();

  settings: SaveBookmarkDialogData = {
    mode: SaveBookmarkDialogMode.SaveStory,
  };

  isNew: boolean;
  isOwner: boolean;
  saveBookmarkDialogMode = SaveBookmarkDialogMode;
  canAddTeamBookmarks = false;

  groupModule: boolean;

  private ngUnsubscribe = new Subject();

  constructor(
    private store: Store,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<SaveBookmarkDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: SaveBookmarkDialogData,
    public snackbar: MatSnackBar,
    private actions: Actions,
    private organizationsService: OrganizationsService,
    private storyService: StoryService,
    private changeRef: ChangeDetectorRef,
  ) {
    this.settings = {
      ...this.settings,
      ...data,
    };

    const bookmark = this.settings.bookmark;
    this.groupModule = bookmark ? PermissionsUtil.isGroupModule(bookmark) : false;

    this.form = this.fb.group({
      title: [
        { value: '', disabled: this.groupModule },
        [Validators.required, Validators.maxLength(60)],
      ],
      collections: new FormArray([]),
      groupAccess: [{ value: null, disabled: this.groupModule }],
      publicAccess: [false],
    });

    this.collectionsArray = this.form.get('collections') as FormArray;

    // Listen for bookmark actions
    this.actions
      .pipe(ofActionDispatched(SaveBookmarkSuccess), takeUntil(this.ngUnsubscribe))
      .subscribe((response) => this.handleSaveSuccess(response));

    this.actions
      .pipe(ofActionDispatched(SaveBookmarkFail), takeUntil(this.ngUnsubscribe))
      .subscribe((response) => this.handleSaveFailure(response.bookmarkEntry.title));

    this.actions
      .pipe(ofActionDispatched(CopyBookmarkSuccess), takeUntil(this.ngUnsubscribe))
      .subscribe((response) => this.handleCopySuccess(response));

    this.actions
      .pipe(ofActionDispatched(CopyBookmarkFailure), takeUntil(this.ngUnsubscribe))
      .subscribe((response) => this.handleCopyFailure(response));

    if (this.groupModule) {
      this.actions
        .pipe(ofActionDispatched(UpdateBookmarkCollectionsSuccess), takeUntil(this.ngUnsubscribe))
        .subscribe((response) => {
          this.handleSaveSuccess(response);
          // Updates the current collection view
          this.store.dispatch(new GalleryActions.SearchStudio());
        });

      this.actions
        .pipe(ofActionDispatched(UpdateBookmarkCollectionsError), takeUntil(this.ngUnsubscribe))
        .subscribe((response) => this.handleSaveFailure(response.bookmark.title));
    }

    this.submit
      .pipe(debounceTime(100), takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.onDebouncedSubmit());
  }

  ngOnInit() {
    // populate group collections
    const organization = this.store.selectSnapshot(OrganizationsState.getOrganization);

    if (
      // Only make request if we don't already have collections
      organization &&
      !this.store.selectSnapshot(OrganizationsState.getCollections)
    ) {
      this.store.dispatch(new GetCollections(organization.id));
    }

    // set form
    const bookmark = this.settings.bookmark;
    const title = bookmark
      ? bookmark.title
      : this.settings.bookmarkTitle
      ? this.settings.bookmarkTitle
      : '';

    // group access state
    let groupEdit = false;
    let groupView = false;

    const groupAccess = bookmark ? this.getGroupAccess(bookmark) : null;

    if (this.groupModule) {
      groupEdit = false;
      groupView = true;
    } else if (groupAccess) {
      // set group access via data
      groupEdit = groupAccess.member_edit;
      groupView = !groupEdit && groupAccess.visible;
    } else if (!bookmark && organization) {
      // default group users
      groupEdit = false;
      groupView = true;
    }

    // public access: non-group users
    const publicAccess = !this.isGroupUser() && bookmark && bookmark.tags.includes('public');

    this.form.patchValue({
      title: title,
      groupAccess: groupEdit ? 'edit' : groupView ? 'view' : '',
      publicAccess: publicAccess,
    });

    this.trackNameAccessDirty();

    // set mode
    this.isNew = true;
    this.isOwner = true;

    if (bookmark) {
      const userRole = this.store.selectSnapshot(AuthState.getUserRole);
      const canEditBookmark = PermissionsUtil.canEditBookmark(userRole, bookmark);
      if (canEditBookmark) {
        this.isNew = false;
        this.isOwner = true;
      } else {
        this.isNew = true;
        this.isOwner = this.isNew;
      }
    }

    // setup group access
    this.initGroupAccess();

    // setup collections
    this.initCollections();

    // set group access
    this.toggleGroupAccess(groupView, groupEdit);

    // setup permissions
    this.initPermissions();

    // setup cancel operation
    this.dialogRef
      .beforeClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.checkCanceSave();
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  initGroupAccess() {
    const groupAccessField = this.form.get('groupAccess');

    // set collections: via group access
    groupAccessField.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((groupAccess) => {
      const groupView = groupAccess === 'view';
      const groupEdit = groupAccess === 'edit';
      this.toggleGroupAccess(groupView, groupEdit);
    });
  }

  initCollections() {
    const bookmark = this.settings.bookmark;
    const organization = this.store.selectSnapshot(OrganizationsState.getOrganization);

    let contentCollections$ = of([]);

    if (organization && bookmark) {
      this.collectionsLoading = true;

      contentCollections$ = this.organizationsService.getCollections({
        group_id: organization.id,
        content_type: bookmark.type,
        content_id: bookmark.id,
        include_content: 1,
      });
    }

    combineLatest([this.collections$, contentCollections$])
      .pipe(
        filter(([collections]) => {
          return !isNil(collections);
        }),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe(([collections, contentCollections]) => {
        contentCollections.forEach((contentCollection: CollectionModel) => {
          this.contentCollections.set(
            contentCollection.collection_id,
            get(contentCollection, 'collection_content[0].collection_content_id', null),
          );
        });

        this.collectionsArray.clear();

        collections.forEach((collection) => {
          this.collectionsMap.set(collection.collection_id, collection);

          const collectionContentId = this.contentCollections.get(collection.collection_id);

          this.collectionsArray.push(
            this.fb.group({
              title: collection.collection_name,
              id: collection.collection_id,
              checked: [
                {
                  value: !isNil(collectionContentId),
                  disabled: !this.collectionsEnabled,
                },
              ],
            }),
          );
        });

        this.trackCollectionsDirty();

        this.collectionsLoading = false;

        this.changeRef.markForCheck();
      });
  }

  trackNameAccessDirty() {
    const initialValue = {
      title: this.form.value.title,
      groupAccess: this.form.value.groupAccess,
      publicAccess: this.form.value.publicAccess,
    };

    this.form.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((formValues) => {
      const currentValue = {
        title: formValues.title,
        groupAccess: formValues.groupAccess,
        publicAccess: formValues.publicAccess,
      };

      this.nameAccessDirty = !isEqual(initialValue, currentValue);
      this.setDisablePristine();
    });
  }

  trackCollectionsDirty() {
    const initialValue = this.collectionsArray.value;
    this.collectionsArray.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((collectionsValues) => {
        this.collectionsDirty = !isEqual(initialValue, collectionsValues);
        this.setDisablePristine();
      });
  }

  initPermissions() {
    combineLatest([this.userRole$, this.organization$])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([userRole, organization]) => {
        this.canAddTeamBookmarks =
          !isNil(organization) &&
          PermissionsUtil.hasPermission(userRole, UserPermissionEnum.AddTeamBookmarks);
        this.changeRef.markForCheck();
      });
  }

  toggleGroupAccess(groupView: boolean, groupEdit: boolean) {
    const canEditCollections = this.isOwner || this.isNew;
    const isGroupAccess = groupView || groupEdit;

    const enabled = canEditCollections && isGroupAccess;
    this.setCollectionsEnabled(enabled);
  }

  handleSubmit() {
    if (this.form.valid) {
      this.submit.next();
    }
  }

  onDebouncedSubmit() {
    const bookmark = this.settings.bookmark;
    const bookmarkEntry = this.getBookmarkEntry();

    switch (this.settings.mode) {
      case SaveBookmarkDialogMode.Copy: {
        const lang = this.store.selectSnapshot(AuthState.getUserLanguage);

        const groupAccess = this.canAddTeamBookmarks
          ? get(bookmarkEntry, 'add_group_access[0]')
          : undefined;

        const addCollections = this.canAddTeamBookmarks ? bookmarkEntry.addCollections : undefined;

        this.store.dispatch(
          new CopyBookmark(
            bookmarkEntry.id,
            bookmarkEntry.title,
            bookmark.type,
            lang,
            groupAccess,
            addCollections,
          ),
        );

        break;
      }
      case SaveBookmarkDialogMode.SaveStory:
      case SaveBookmarkDialogMode.Manage: {
        // We can only update the collections that a group module belongs to
        if (this.groupModule) {
          this.store.dispatch(
            new UpdateBookmarkCollections(
              bookmark,
              bookmarkEntry.addCollections,
              bookmarkEntry.removeCollections,
            ),
          );
        } else {
          this.saveBookmark(bookmark, bookmarkEntry);
        }

        break;
      }
    }
  }

  saveBookmark(bookmark: BookmarkModel, bookmarkEntry: BookmarkEntryModel) {
    const includeIndex = this.settings.mode === SaveBookmarkDialogMode.SaveStory;

    // manage: get story to sync displayName/title
    const story$ =
      bookmark && bookmark.id && this.settings.mode === SaveBookmarkDialogMode.Manage
        ? this.storyService.getStory(bookmark.id, bookmark.type)
        : of(undefined);

    story$.pipe(take(1), takeUntil(this.ngUnsubscribe)).subscribe((story: StoryModel) => {
      // update index (i.e., story data) w/ updated bookmark title
      if (story && story.displayName !== bookmarkEntry.title) {
        bookmarkEntry.index = {
          ...story,
          displayName: bookmarkEntry.title,
        };
      }

      // save/update bookmark
      this.store.dispatch(new SaveBookmark(bookmarkEntry, false, includeIndex));
    });
  }

  handleCancelClick() {
    this.dialogRef.close();
  }

  handleSaveSuccess(action: SaveBookmarkSuccess | UpdateBookmarkCollectionsSuccess) {
    const bookmark = action.bookmark;
    const message =
      this.settings.mode === SaveBookmarkDialogMode.Manage
        ? `Your updates to "${bookmark.title}" have been saved to your library.`
        : `"${bookmark.title}" saved to your library.`;

    this.snackbar.open(message);
    this.dialogRef.close(bookmark);
  }

  handleSaveFailure(title: string) {
    this.snackbar.open(`Unable to save bookmark "${title}". Please try again.`);
  }

  handleCopySuccess(action: CopyBookmarkSuccess) {
    const message = `"${action.newBookmark.title}" saved to your library.`;
    this.snackbar.open(message);
    this.dialogRef.close(action.newBookmark);
  }

  handleCopyFailure(action: CopyBookmarkFailure) {
    const message = get(action.error, 'error.error.message');

    switch (message) {
      case 'Action forbidden. Your account content limit has been reached':
        this.store.dispatch(new DialogActions.OpenLimit());
        break;
      default:
        this.snackbar.open('There was an error adding to your library');
        break;
    }
  }

  private getBookmarkEntry(): BookmarkEntryModel {
    const { bookmark, viewMode } = this.settings;
    const { title, groupAccess, publicAccess } = this.form.value;
    const groupView = groupAccess === 'view';
    const groupEdit = groupAccess === 'edit';
    const isPublicAccess = this.isGroupUser() ? null : publicAccess;

    let addGroupAccess = [];
    let removeGroupAccess = [];

    // Handle collections
    const addCollections = [];
    const removeCollections = new Map();

    // owner: manage permissions + collections
    if (this.isOwner) {
      const groupAccessPermission = this.createGroupAccess(groupView, groupEdit);
      if (groupAccessPermission) {
        // set group access if selected
        addGroupAccess = [groupAccessPermission];
      } else {
        // remove all existing access if none selected
        removeGroupAccess = bookmark ? bookmark.group_access : [];
      }

      this.collectionsArray.value.forEach((collectionValue) => {
        const collection = this.collectionsMap.get(collectionValue.id);
        const collectionContentId = this.contentCollections.get(collectionValue.id);

        if (this.collectionsEnabled) {
          // With collections enabled, create a diff between the
          // Bookmark's current collections and the current form state
          if (isNil(collectionContentId) && collectionValue.checked) {
            addCollections.push(collection);
          } else if (!isNil(collectionContentId) && !collectionValue.checked) {
            removeCollections.set(collection, collectionContentId);
          }
        } else {
          // Otherwise just remove all of the bookmark's current collections
          if (!isNil(collectionContentId)) {
            removeCollections.set(collection, collectionContentId);
          }
        }
      });
    }

    return {
      isOwner: bookmark ? this.isOwner : true,
      id: bookmark ? bookmark.id : null,
      title: title,
      viewMode: viewMode,
      add_group_access: addGroupAccess,
      remove_group_access: removeGroupAccess,
      addCollections: addCollections,
      removeCollections: removeCollections,
      public_access: isPublicAccess,
    };
  }

  private getGroupAccess(bookmark: BookmarkModel): BookmarkGroupModel {
    const organization = this.store.selectSnapshot(OrganizationsState.getOrganization);
    let groupAccess = null;
    if (organization && bookmark.group_access) {
      const organizationId = organization.id;
      groupAccess = bookmark.group_access.find(
        (bookmarkAccessLevel) => bookmarkAccessLevel.group_id === organizationId,
      );
    }
    return groupAccess;
  }

  private createGroupAccess(groupView: boolean, groupEdit: boolean): BookmarkGroupModel {
    const organization = this.store.selectSnapshot(OrganizationsState.getOrganization);
    const isValidAccess = groupView || groupEdit;
    if (organization && isValidAccess) {
      return {
        group_id: organization.id,
        member_edit: groupEdit,
        visible: groupView || groupEdit,
      };
    } else {
      return null;
    }
  }

  private isGroupUser(): boolean {
    return !!this.store.selectSnapshot(OrganizationsState.getOrganization);
  }

  private checkCanceSave() {
    // cancel saving if applicable
    const saving =
      this.settings.mode === SaveBookmarkDialogMode.Copy
        ? this.store.selectSnapshot(BookmarksState.getCopying)
        : this.store.selectSnapshot(BookmarksState.getSaving);

    if (saving) {
      this.store.dispatch(
        this.settings.mode === SaveBookmarkDialogMode.Copy
          ? new CancelCopyBookmark()
          : new CancelSaveBookmark(),
      );
    }
  }

  private setCollectionsEnabled(enabled: boolean) {
    this.collectionsEnabled = enabled;

    this.collectionsArray.controls.forEach((control) => {
      if (enabled) {
        control.enable();
      } else {
        control.disable();
      }
    });
  }

  private setDisablePristine() {
    this.disablePristine =
      this.settings.mode === SaveBookmarkDialogMode.Manage &&
      !this.collectionsDirty &&
      !this.nameAccessDirty;
    this.changeRef.markForCheck();
  }
}
